// Native
import moment from "moment"

// Custom
import {
    Badge,
    Box,
    Button,
    ButtonGroup,
    DataList,
    DialogBody,
    DialogFooter,
    Flex,
    FormatNumber,
    Separator,
    Stack,
    Text,
    useDisclosure
} from "@chakra-ui/react"

import { API } from "config";
import { Form, Parts, UI } from "components"
import { Styles } from "config"
import useWithdrawals from "hooks/useWithrawals"
import { useRef, useState } from "react";
import { toaster } from "config";
import useOptions from "hooks/useOptions";

export default function View({ data, ...props }){

    const withdrawals = useWithdrawals()
    const options = useOptions();
    const cancelDialog = useDisclosure()
    const status = Styles.withdrawStatus(data?.status)
    const submitRef = useRef()

    const [loading, setLoading] = useState()
    const [fiscalNote, setFiscalNote] = useState()

    const handleSubmit = async () => {
        if(!window.confirm('Tem certeza que deseja anexar a nota fiscal?')) return
        setLoading(true)

        try {
            const formData = new FormData;
            formData.append('file', fiscalNote);
            await API.post(`/withdrawals/attach-fiscal-note/${data?.id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            })
            toaster.create({ title: 'Nota Fiscal anexada com sucesso', type: 'success' })
            withdrawals.refetch()
            props.onClose()
            setFiscalNote(null)
        } catch (err) {
            toaster.create({
                title: 'Não foi possível anexar a nota fiscal',
                description: err?.response?.data?.message,
                type: 'warning'
            })
        } finally {
            setLoading(false);
        }
    };

    return(
        <>
            <Parts.Dialog.Withdraws.Cancel data={data} {...cancelDialog} />

            <UI.Dialog size="sm" title={`Pedido de saque #${data?.cod}`} {...props}>
                <DialogBody>
                    <Stack gap={4}>
                        <Flex justifyContent="space-between" alignItems="center">
                            <Box>
                                <Text children="Valor solicitado:" color="GrayText" />
                                <Text fontSize="2xl"><UI.Number.Amount value={data?.total} /></Text>
                            </Box>
                            <Box>
                                <Text children="Status:" color="GrayText" />
                                <Badge size="lg" children={data?.status_label} colorPalette={status.colorPalette} />
                            </Box>
                        </Flex>

                        <Separator />
                        <Text children="Conta bancária:" color="GrayText" />
                        <UI.Bank data={data?.bank} />
                        <Separator />

                        <DataList.Root orientation="horizontal">
                            <DataList.Item>
                                <DataList.ItemLabel children="Valor da taxa:" />
                                <DataList.ItemValue children={<UI.Number.Amount value={data?.tax} />} />
                            </DataList.Item>
                            <DataList.Item>
                                <DataList.ItemLabel children="Data do pedido:" />
                                <DataList.ItemValue children={moment(data?.created_at).format('LLL')} />
                            </DataList.Item>
                            <UI.Show visible={[4,5].includes(data?.status)}>
                                <DataList.Item>
                                    <DataList.ItemLabel children="Data do cancelamento:" />
                                    <DataList.ItemValue children={moment(data?.canceled_at ?? data?.rejected_at).format('LLL')} />
                                </DataList.Item>
                            </UI.Show>
                            <UI.Show visible={data?.status === 5}>
                                <DataList.Item>
                                    <DataList.ItemLabel children="Motivo da rejeição:" />
                                    <DataList.ItemValue children={data?.rejected_reason} />
                                </DataList.Item>
                            </UI.Show>
                            <UI.Show visible={data.status === 1}>
                                <DataList.Item>
                                    <DataList.ItemLabel children="Depositado em:" />
                                    <DataList.ItemValue children={moment(data?.sent_at).format('LLL')} />
                                </DataList.Item>
                            </UI.Show>
                        </DataList.Root>

                        {/* Alerta de obrigatoriedade da nota fiscal */}
                        <UI.Show visible={data?.total >= 1930 * 100 && !data.fiscal_note?.id}>
                            <UI.Alert size="sm" status="warning" title="Necessário nota fiscal">
                                Para saques acima de <b>R$1.930,00</b>, anexe a nota fiscal.
                            </UI.Alert>
                        </UI.Show>

                        {/* Alerta de prazo/limite: Visível apenas se estiver DENTRO do prazo */}
                        <UI.Show
                            visible={data?.total >= 1930 * 100 && !data?.fiscal_note?.id && options.data?.isNoteAvailable}
                        >
                            <UI.Alert size="sm" status="info" title="Prazo para envio">
                                A nota fiscal deve ser enviada até o dia <b>{options.data?.rawNoteDays || "--"}</b> deste mês.
                            </UI.Alert>
                        </UI.Show>

                        {/* Alerta de erro: Visível apenas se o prazo já TIVER EXPIRADO */}
                        <UI.Show
                            visible={data?.total >= 1930 * 100 && !data?.fiscal_note?.id && !options.data?.isNoteAvailable}
                        >
                            <UI.Alert size="sm" status="error" title="Prazo expirado">
                                O prazo para envio encerrou (limite dia {options.data?.rawNoteDays}).
                                Este pedido será <b>estornado</b> pelo financeiro.
                            </UI.Alert>
                        </UI.Show>

                        {/* Alerta de sucesso: Exibe o nome do arquivo quando já anexado */}
                        <UI.Show visible={data?.total >= 1930 * 100 && !!data?.fiscal_note?.id}>
                            <UI.Alert size="sm" status="success" title="Nota Fiscal anexada">
                                O arquivo <b>{data?.fiscal_note?.label}</b> já foi enviado com sucesso.
                            </UI.Alert>
                        </UI.Show>

                        {/* Formulário de Upload (Só aparece se estiver no prazo e sem nota) */}
                        <UI.Show
                            visible={
                                data?.total >= 1930 * 100 &&
                                options.data?.isNoteAvailable &&
                                !data?.fiscal_note?.id &&
                                data?.status === 2
                            }
                        >
                            <Form.Container submitRef={submitRef} onSubmit={handleSubmit}>
                                <Form.Upload.Button
                                    required
                                    label="Anexar nota fiscal"
                                    placeholder="Clique aqui para anexar"
                                    disabled={loading}
                                    onFileChange={e => setFiscalNote(e.acceptedFiles?.[0])}
                                    helper="Apenas arquivos .pdf"
                                />
                                <Button
                                    type="button"
                                    colorPalette="orange"
                                    loading={loading}
                                    onClick={() => submitRef.current.click()}
                                    width="full"
                                >
                                    Enviar Nota Fiscal
                                </Button>
                            </Form.Container>
                        </UI.Show>
                    </Stack>
                </DialogBody>

                <DialogFooter>
                    <ButtonGroup size="xs" disabled={withdrawals.isFetching}>
                        <UI.Show visible={data?.status === 2}>
                            <Button colorPalette="red" onClick={cancelDialog.onOpen}>
                                <UI.Icon name="cancel" /> Cancelar pedido de saque
                            </Button>
                        </UI.Show>
                    </ButtonGroup>
                </DialogFooter>
            </UI.Dialog>
        </>
    )
}